import moment from 'moment';
import React, { FunctionComponent, useEffect } from 'react';
import { Link, NavLink } from 'react-router';
import useBreadcrumbs, { BreadcrumbMatch } from 'use-react-router-breadcrumbs';
import { getDocName } from '~/agreements/Sign';
import { api, useResource } from '~/api';
import logoImg from '~/assets/icons/logo-full.svg';
import { useAuthData } from '~/auth';
import { AuthUser } from '~/auth/user.ts';
import { NotificationsMenu } from '~/components/layout/Notifications';
import { OpenToOpportunitiesMenu } from '~/components/layout/OpenToOpportunitiesMenu';
import UserMenu from '~/components/layout/UserMenu';
import Divider from '~/components/new/Divider';
import { isStaging } from '~/utils/env';
import { fullName, monthName, toTitle } from '~/utils/format';
import { classNames } from '~/utils/styles';

function TopBar({
    user,
    refreshUser = () => {},
    hideBreadcrumbs = false,
    showLogo = false,
}: {
    user: AuthUser;
    refreshUser?: () => void;
    hideBreadcrumbs?: boolean;
    showLogo?: boolean;
}) {
    return (
        <div
            className={classNames({
                topbar: true,
                'topbar-staging': isStaging(),
            })}
            data-test-component='layout/TopBar'>
            {showLogo && (
                <Link
                    to='/'
                    className='topbar-brand'>
                    <img
                        src={logoImg}
                        alt='Remotely'
                    />
                </Link>
            )}
            {!hideBreadcrumbs && <Breadcrumbs />}
            {user && (
                <>
                    {!user.isExContractor() && (
                        <div className='topbar-actions'>
                            <OpenToOpportunitiesMenu refreshUser={refreshUser} />
                            {user.candidate_id && <Divider vertical />}
                            <NotificationsMenu />
                        </div>
                    )}
                    <UserMenu />
                </>
            )}
        </div>
    );
}

export function useTitle(title: string) {
    const { authenticated } = useAuthData();
    useEffect(() => {
        setTitle(authenticated, title);
        return () => {
            document.title = 'Remotely';
        };
    }, [authenticated, title]);
}

export function setTitle(authenticated: boolean, title: string) {
    document.title = authenticated ? title : `Remotely - ${title}`;
}

type BreadcrumbComponent = FunctionComponent<{ match: BreadcrumbMatch }>;

type RouteConfig = {
    path: string;
    url?: string;
    breadcrumb:
        string | BreadcrumbComponent | ((opts: { match: { params: any } }) => string | null);
};

export const routeConfig: RouteConfig[] = [
    {
        path: '/admin',
        breadcrumb: 'Admin',
    },
    {
        path: '/admin/background-checks',
        breadcrumb: 'Background checks',
    },
    {
        path: '/admin/backoffice',
        breadcrumb: 'Backoffice',
    },
    {
        path: '/admin/lines',
        breadcrumb: 'Line items',
    },
    {
        path: '/admin/revenue',
        breadcrumb: 'Revenue',
    },
    {
        path: '/admin/revenue/remotely-fee',
        breadcrumb: 'Remotely Fees',
    },
    {
        path: '/admin/offer-letters',
        breadcrumb: 'Offer letters',
    },
    {
        path: '/admin/offer-letters/details/:id',
        breadcrumb: 'Details',
    },
    {
        path: '/admin/approvals',
        breadcrumb: 'Approvals',
    },
    {
        path: '/admin/customers',
        breadcrumb: 'Customers',
    },
    {
        path: '/admin/customers/deposits',
        breadcrumb: 'Deposits',
    },
    {
        path: '/admin/customers/deposits/balance',
        breadcrumb: 'By customer',
    },
    {
        path: '/admin/customers/deposits/contractors',
        breadcrumb: 'By contractor',
    },
    {
        path: '/admin/customers/applications',
        breadcrumb: 'Sales Opportunities',
    },
    {
        path: '/admin/customers/expenses',
        breadcrumb: 'Expenses',
    },
    {
        path: '/admin/customers/expenses/create',
        breadcrumb: 'Create expense',
    },
    {
        path: '/admin/customers/expenses/:id',
        breadcrumb: 'Details',
    },
    {
        path: '/admin/backoffice/:tableId',
        breadcrumb: function Breadcrumb({ match }: { match: BreadcrumbMatch }) {
            return toTitle(match.params.tableId as string);
        },
    },
    {
        path: '/admin/backoffice/:tableId/details/:id',
        breadcrumb: 'Details',
    },
    {
        path: '/admin/equipment',
        breadcrumb: 'Equipment',
    },
    {
        path: '/admin/equipment/request',
        breadcrumb: 'Request',
    },
    {
        path: '/admin/equipment/request/:quoteID',
        breadcrumb: ({ match }: { match: { params: any } }) => match.params.quoteID,
    },
    {
        path: '/admin/equipment/quotes',
        breadcrumb: 'Quotes',
    },
    {
        path: '/admin/equipment/quotes/:id',
        breadcrumb: ({ match }: { match: { params: any } }) => match.params.id,
    },
    {
        path: '/admin/equipment/quotes/create',
        breadcrumb: 'Create a quote',
    },
    {
        path: '/admin/applicants',
        breadcrumb: 'Applicants',
    },
    {
        path: '/admin/talent-leads',
        breadcrumb: 'Talent Leads',
    },
    {
        path: '/admin/candidates',
        breadcrumb: 'Candidates',
    },
    {
        path: '/admin/candidates/add',
        breadcrumb: 'Add candidate',
    },
    {
        path: '/admin/contractors',
        breadcrumb: 'Contractors',
    },
    {
        path: '/admin/contractors/invoices',
        breadcrumb: 'Invoices',
    },
    {
        path: '/admin/contractors/invoices/payments',
        breadcrumb: 'Payments',
    },
    {
        path: '/admin/contractors/:id',
        breadcrumb: withBreadcrumbTitle<{ title: string }>(
            `/contractors/:id/breadcrumb`,
            (data) => data.title,
        ),
    },
    {
        path: '/admin/customers/active',
        breadcrumb: 'Active customers',
    },
    {
        path: '/admin/customers/invoices',
        breadcrumb: 'Invoices',
    },
    {
        path: '/admin/customers/invoices/due',
        breadcrumb: 'Due invoices',
    },
    {
        path: '/admin/customers/:id',
        breadcrumb: withBreadcrumbTitle<{ title: string }>(
            `/customer/:id/breadcrumb`,
            (data) => data.title,
        ),
    },
    {
        path: '/admin/customers/:id/billing',
        breadcrumb: 'Billing',
    },
    {
        path: '/admin/customers/:id/remotely',
        breadcrumb: 'Remotely',
    },
    {
        path: '/admin/customers/:id/pricing',
        breadcrumb: 'Pricing',
    },
    {
        path: '/admin/customers/:id/pricing/add/management',
        breadcrumb: 'Create management fee',
    },
    {
        path: '/admin/customers/:id/pricing/add/open_position',
        breadcrumb: 'Create open position fee',
    },
    {
        path: '/admin/customers/:id/pricing/discount/:pricingId',
        breadcrumb: 'Add discount',
    },
    {
        path: '/admin/customers/:id/pricing/edit/:pricingId',
        breadcrumb: 'Edit fee',
    },
    {
        path: '/admin/customers/:id/services',
        breadcrumb: 'Services',
    },
    {
        path: '/admin/customers/:id/contractors',
        breadcrumb: 'Active contractors',
    },
    {
        path: '/admin/customers/:id/invoices',
        url: '/admin/customers/invoices',
        breadcrumb: 'Invoices',
    },
    {
        path: '/admin/customers/:customerId/invoices/:invoiceId',
        breadcrumb: 'Invoice details',
    },
    {
        path: '/admin/tags',
        breadcrumb: 'Tags',
    },
    {
        path: '/admin/contractors/onboarding',
        breadcrumb: 'Onboarding',
    },
    {
        path: '/admin/tags/merge',
        breadcrumb: 'Merge tags',
    },
    {
        path: '/admin/holidays',
        breadcrumb: 'Holidays',
    },
    {
        path: '/admin/references',
        breadcrumb: 'Reference Checks',
    },
    {
        path: '/admin/checkins/customers',
        breadcrumb: 'Check-Ins',
    },
    {
        path: '/admin/checkins/contractors',
        breadcrumb: 'Check-Ins',
    },
    {
        path: '/admin/referrals',
        breadcrumb: 'Referrals',
    },
    {
        path: '/admin/insurance',
        breadcrumb: 'Health insurance',
    },
    {
        path: '/admin/recordings',
        breadcrumb: 'Recordings',
    },
    {
        path: '/tasks',
        breadcrumb: 'Tasks',
    },
    {
        path: '/invoices',
        breadcrumb: 'Invoices',
    },
    {
        path: '/invoices/details/:id',
        breadcrumb: withBreadcrumbTitle<{ month: number; year: number }>(
            `/contractor/invoices/:id`,
            (data) => `${monthName(data.month)} ${data.year}`,
        ),
    },
    {
        path: '/invoices/edit/:id',
        breadcrumb: 'Edit invoice',
    },
    {
        path: '/timeoff',
        breadcrumb: 'Paid time off',
    },
    {
        path: '/timeoff/request',
        breadcrumb: 'Request time off',
    },
    {
        path: '/timeoff',
        breadcrumb: 'Paid time off',
    },
    {
        path: '/timeoff/requests',
        breadcrumb: 'Requests',
    },
    {
        path: '/timeoff/calendar',
        breadcrumb: 'Calendar',
    },
    {
        path: '/profile',
        breadcrumb: 'Profile',
    },
    {
        path: '/profile/personal-info/edit',
        breadcrumb: 'Edit personal info',
    },
    {
        path: '/profile/certifications/add',
        breadcrumb: 'Add certifications',
    },
    {
        path: '/profile/contact-info/edit',
        breadcrumb: 'Edit contact info',
    },
    {
        path: '/profile/location/edit',
        breadcrumb: 'Edit location',
    },
    {
        path: '/profile/work-info/edit',
        breadcrumb: 'Edit work info',
    },
    {
        path: '/profile/work-profile/edit',
        breadcrumb: 'Edit professional info',
    },
    {
        path: '/profile/calendly/edit',
        breadcrumb: 'Add Calendly account',
    },
    {
        path: '/profile/scheduling-url/edit',
        breadcrumb: 'Add scheduling account',
    },
    {
        path: '/profile/bank-accounts/:id/edit',
        breadcrumb: 'Edit bank account',
    },
    {
        path: '/profile/bank-accounts/new',
        breadcrumb: 'Create bank account',
    },
    {
        path: '/profile/equipment',
        breadcrumb: 'Confirm your equipment',
    },
    {
        path: '/profile/equipment/sign',
        breadcrumb: 'Sign the agreements',
    },
    {
        path: '/profile/shipping-info/verify',
        breadcrumb: 'Shipping info',
    },
    {
        path: '/profile/shipping-info/:id/verify',
        breadcrumb: 'Shipping info',
    },
    {
        path: '/profile/builder',
        breadcrumb: 'Builder',
    },
    {
        path: '/agreement/:id/sign/:type',
        breadcrumb: function Breadcrumb({ match }: { match: BreadcrumbMatch }) {
            return getDocName(match.params.type as string);
        },
    },
    {
        path: '/agreement/:id/sign',
        breadcrumb: 'Agreement',
    },
    {
        path: '/expenses',
        breadcrumb: 'Expenses',
    },
    {
        path: '/expenses/details/:id',
        breadcrumb: 'Expense detail',
    },
    {
        path: '/expenses/new',
        breadcrumb: 'Submit expense',
    },
    {
        path: '/customer/company',
        breadcrumb: withBreadcrumbTitle<{ title: string }>(
            `/customer/breadcrumb`,
            (data) => data.title,
        ),
    },
    {
        path: '/customer/company/policies-benefits/add',
        breadcrumb: 'Set Policies & Benefits',
    },
    {
        path: '/customer/company/policies-benefits/edit',
        breadcrumb: 'Edit Policies & Benefits',
    },
    {
        path: '/customer/company/pto',
        breadcrumb: 'PTO',
    },
    {
        path: '/customer/company/users',
        breadcrumb: 'Users',
    },
    {
        path: '/customer/company/groups',
        breadcrumb: 'Groups',
    },
    {
        path: '/customer/timeoff',
        breadcrumb: 'Paid time off',
    },
    {
        path: '/customer/timeoff/requests',
        breadcrumb: 'Requests',
    },
    {
        path: '/customer/timeoff/calendar',
        breadcrumb: 'Calendar',
    },
    {
        path: '/customer/timeoff/settings',
        breadcrumb: 'Settings',
    },
    {
        path: '/customer/expenses',
        breadcrumb: 'Expenses',
    },
    {
        path: '/customer/expenses/details/:id',
        breadcrumb: 'Expense detail',
    },
    {
        path: '/customer/equipment',
        breadcrumb: 'Equipment',
    },
    {
        path: '/customer/equipment/quotes',
        breadcrumb: 'Quotes',
    },
    {
        path: '/customer/equipment/quotes/:id',
        breadcrumb: ({ match }: { match: { params: any } }) => match.params.id,
    },
    {
        path: '/customer/equipment/request',
        breadcrumb: 'Request',
    },
    {
        path: '/customer/equipment/request/new',
        breadcrumb: 'New',
    },
    {
        path: '/customer/equipment/request/new/:id',
        breadcrumb: withBreadcrumbTitle<{ first_name: string; last_name: string }>(
            `/contractors/contract/:id`,
            (data) => fullName(data),
        ),
    },
    {
        path: '/customer/equipment/request/:quoteID/edit',
        breadcrumb: ({ match }: { match: { params: any } }) =>
            `Edit request ${match.params.quoteID}`,
    },
    {
        path: '/customer/equipment/request/:quoteID',
        breadcrumb: ({ match }: { match: { params: any } }) => match.params.quoteID,
    },
    {
        path: '/customer/contractors',
        breadcrumb: 'Contractors',
    },
    {
        path: '/customer/contractors/:id',
        breadcrumb: withBreadcrumbTitle<{ title: string }>(
            `/contractors/:id/breadcrumb`,
            (data) => data.title,
        ),
    },
    {
        path: '/customer/documents',
        breadcrumb: 'Documents',
    },
    {
        path: '/customer/documents/agreements/CA',
        breadcrumb: 'Consulting Agreements',
    },
    {
        path: '/customer/documents/agreements/EA',
        breadcrumb: 'Equipment Agreements',
    },
    {
        path: '/customer/documents/agreements/termination',
        breadcrumb: 'Termination Agreements',
    },
    {
        path: '/customer/documents/agreements/tax_form',
        breadcrumb: 'Tax Forms',
    },
    {
        path: '/customer/documents/offers',
        breadcrumb: 'Offer Letters',
    },
    {
        path: '/customer/documents/agreements/code_of_conduct',
        breadcrumb: 'Codes of Conduct',
    },
    {
        path: '/customer/documents/agreements/other',
        breadcrumb: 'Other',
    },
    {
        path: '/opportunities',
        breadcrumb: 'Job opportunities',
    },
    {
        path: '/position',
        url: '/opportunities',
        breadcrumb: 'Job opportunities',
    },
    {
        path: '/position/:id',
        breadcrumb: withBreadcrumbTitle<{ title: string }>(
            `/opportunities/openings/:id/breadcrumb`,
            (data) => data.title,
        ),
    },
    {
        path: '/position/:id/refer',
        breadcrumb: 'Refer a developer',
    },
    {
        path: '/company',
        url: '/opportunities/startups',
        breadcrumb: 'Job opportunities',
    },
    {
        path: '/company/:id',
        breadcrumb: withBreadcrumbTitle<{ title: string }>(
            `/customer/:id/breadcrumb`,
            (data) => data.title,
        ),
    },
    {
        path: '/job_positions',
        breadcrumb: 'Job Positions',
    },
    {
        path: '/job_positions/applications',
        breadcrumb: 'Applications',
    },
    {
        path: '/job_positions/open',
        breadcrumb: 'Open',
    },
    {
        path: '/job_positions/closed',
        breadcrumb: 'Closed',
    },
    {
        path: '/job_positions/new',
        breadcrumb: 'New Position',
    },
    {
        path: '/job_positions/table',
        url: '/job_positions',
        breadcrumb: 'Job Positions',
    },
    {
        path: '/job_positions/list',
        url: '/job_positions',
        breadcrumb: 'Job Positions',
    },
    {
        path: '/job_positions/settings',
        breadcrumb: 'Settings',
    },
    {
        path: '/job_positions/settings/contacts',
        breadcrumb: 'Contacts',
    },
    {
        path: '/job_positions/settings/tags',
        breadcrumb: 'Tags',
    },
    {
        path: '/job_positions/:id/edit',
        breadcrumb: 'Edit',
    },
    {
        path: '/job_positions/:id',
        breadcrumb: withBreadcrumbTitle<{ title: string }>(
            `/job_positions/breadcrumb/:id`,
            (data) => data.title,
        ),
    },
    {
        path: '/applications',
        breadcrumb: 'Job applications',
    },
    {
        path: '/applications/:id',
        breadcrumb: withBreadcrumbTitle<{ title: string }>(
            `/opportunities/openings/:id/breadcrumb`,
            (data) => data.title,
        ),
    },
    {
        path: '/offers/:id',
        breadcrumb: 'Offer letter',
    },
    {
        path: '/offers/:id/sign/CA',
        breadcrumb: 'Sign Consulting Agreement',
    },
    {
        path: '/offers/:id/sign/tax_form',
        breadcrumb: 'Sign Tax Form',
    },
    {
        path: '/candidates',
        breadcrumb: 'Candidates',
    },
    {
        path: '/candidates/temp-dashboard',
        breadcrumb: 'For Review',
    },
    {
        path: '/candidates/:id',
        breadcrumb: withBreadcrumbTitle<{ title: string }>(
            `/candidates/:id/breadcrumb`,
            (data) => data.title,
        ),
    },
    {
        path: '/candidates/:id/profile',
        breadcrumb: 'Profile',
    },
    {
        path: '/referrals',
        breadcrumb: 'Referrals',
    },
    {
        path: '/referrals/terms',
        breadcrumb: 'Terms',
    },
    {
        path: '/referrals/refer',
        breadcrumb: 'Refer a developer',
    },
    {
        path: '/referrals/startup/refer',
        breadcrumb: 'Refer & Earn',
    },
    {
        path: '/settings',
        breadcrumb: 'Settings',
    },
    {
        path: '/settings/:tab',
        breadcrumb: ({ match }: { match: { params: any } }) => toTitle(match.params.tab as string),
    },
    {
        path: '/references',
        breadcrumb: 'References',
    },
    {
        path: '/references/:id',
        breadcrumb: withBreadcrumbTitle<string>('/references/:id/breadcrumb', (data) => data),
    },
    {
        path: '/recordings',
        breadcrumb: 'Recordings',
    },
    {
        path: '/recordings/:id',
        breadcrumb: function BreadcrumbTitle({ match }: { match: BreadcrumbMatch }) {
            const { user } = useAuthData();
            const id: string = match?.params?.id || '';

            let fn: Promise<{ title: string }>;
            let deps: string[] = [];
            if (id.includes('@')) {
                fn = Promise.resolve({ title: `From: ${id}` });
            } else if (id.length === 8) {
                fn = Promise.resolve({
                    title: `From: ${moment(id, 'YYYYMMDD').format('YYYY/MM/DD')}`,
                });
            } else {
                fn = api(`/recordings/breadcrumb/${id}`);
                deps = [id];
            }

            const { data } = useResource(() => fn, deps);
            if (!data) {
                return null;
            }

            const breadcrumbTitle = data?.title;
            setTitle(user.authenticated, breadcrumbTitle);
            return breadcrumbTitle;
        },
    },
    {
        path: '/admin/profile-builder-calls',
        breadcrumb: 'AI Agents Calls',
    },
    {
        path: '/admin/im/threads',
        breadcrumb: 'WhatsApp threads',
    },
    {
        path: '/admin/im/templates',
        breadcrumb: 'WhatsApp Templates',
    },
    {
        path: '/admin/im/templates/add',
        breadcrumb: 'Add template',
    },
    {
        path: '/admin/im/templates/edit/:tpl_id',
        breadcrumb: 'Edit template',
    },
    {
        path: '/',
        breadcrumb: 'Remotely',
    },
];

const routesWithBreadcrumbs = routeConfig.reduce((set, r) => {
    set.add(r.path);
    return set;
}, new Set());

const Breadcrumbs = function () {
    const { user } = useAuthData();

    const breadcrumbs = useBreadcrumbs(routeConfig);
    let crumbs = breadcrumbs.filter((r) => routesWithBreadcrumbs.has(r.match.pattern.path));
    let title = '';
    if (
        crumbs.length > 0 &&
        crumbs[crumbs.length - 1].breadcrumb &&
        typeof crumbs[crumbs.length - 1].breadcrumb !== 'string'
    ) {
        const crumb = crumbs[crumbs.length - 1];
        React.Children.forEach(crumb.breadcrumb, (child) => {
            if (typeof child === 'string') {
                title = child;
            }
        });
    }

    if (title.length > 0) {
        setTitle(user.authenticated, title);
    }

    crumbs = crumbs.filter((c) => c.key !== '/');
    // Don't show separate crumb for table/list views—just "Job Positions"
    crumbs = crumbs.filter(
        (c) =>
            c.match.pathname !== '/job_positions/table' &&
            c.match.pathname !== '/job_positions/list',
    );

    const environmentName = import.meta.env.VITE_APP_ENVIRONMENT;

    return (
        <nav className='topbar-breadcrumbs'>
            <ul>
                {isStaging() && environmentName === 'staging' && <li>Staging</li>}
                {isStaging() && environmentName !== 'staging' && (
                    <li>
                        Non-Prod (<pre>{environmentName}</pre>)
                    </li>
                )}
                {crumbs.map(({ match, breadcrumb }) => (
                    <li key={match.pathname}>
                        <NavLink to={match.pathname}>{breadcrumb}</NavLink>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

export default TopBar;

function withBreadcrumbTitle<T>(dataUrl: string, getTitle: (data: T) => string) {
    return function BreadcrumbTitle({ match }: { match: { params: { id: string } } }) {
        const { user } = useAuthData();
        const { data } = useResource(
            () => api<T>(dataUrl.replace(/(?<=\/):id(?=(\/|$))/, match.params.id)),
            [match.params.id],
        );

        if (!data) {
            return null;
        }

        const breadcrumbTitle = getTitle(data as T);

        setTitle(user.authenticated, breadcrumbTitle);
        return breadcrumbTitle;
    };
}
